import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { OrderKeys, orderStatusTranslator } from 'sections/LKSection/modules/OrderModule/data'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { ItemBox } from 'sections/LKSection/shared/components/UI/ItemBox/ItemBox'
import { Status, StatusUIType } from 'sections/LKSection/shared/components/UI/Status/Status'
import { Flex, Text } from 'components/uiKit'
import { formateDate } from 'utils/formatting/formateDate/formateDate'
import { getOrderItemRoute } from 'utils/helpers/routes/getRoutes'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { useResize } from 'utils/hooks/useResize'

import { OrderStatus } from '../../types'
import { Timer } from '../Timer/Timer'

import s from './OrderItemHeader.module.scss'

type OrderItemProps = {
  order: Order
}

export const OrderItemHeader = observer(({ order }: OrderItemProps) => {
  const { viewOrderUiStore: uiStore } = useStore()

  const { isTablet, isMobile } = useResize()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [timeLeft, setTimeLeft] = useState(0)
  const [loading, setLoading] = useState(false)

  useAsyncEffect(async () => {
    if (order.status === OrderStatus.waitingPayment && order.id) {
      setLoading(true)
      const seconds = await uiStore.getPaymentDeadline(order.id)
      setLoading(false)

      seconds && setTimeLeft(seconds)
    }
  }, [])

  const getKey = (key: string) => `${OrderKeys.orderPage}.${key}`

  const orderItemClickHandler = () => {
    uiStore.setSelectedOrder(order)

    if (order.id) {
      navigate(getOrderItemRoute(+order.id, uiStore.page, uiStore.pageSize))
    }
  }

  return (
    <ItemBox onClick={orderItemClickHandler}>
      <Flex
        align={isTablet ? 'flex-start' : 'center'}
        justify="space-between"
        gap={isTablet ? 16 : 40}
        vertical={isTablet}
        style={{ width: '100%' }}
      >
        <Flex
          gap={16}
          align={isTablet ? 'flex-start' : 'center'}
          justify={isTablet ? 'space-between' : 'flex-start'}
          style={{ width: '100%' }}
        >
          <Flex className={s.statusWrap} gap={8} align="flex-start" vertical={isMobile}>
            {order.deleted && order.status !== OrderStatus.deleted && (
              <Status title={t(getKey('Удалена'))} type={StatusUIType.red} />
            )}

            <Flex vertical align="center" gap={8}>
              <Status
                title={t(getKey(orderStatusTranslator[order.status].title))}
                type={orderStatusTranslator[order.status]?.type}
              />

              {order.status === OrderStatus.waitingPayment && (
                <Timer type="timeLeft" timeLeftInSeconds={timeLeft} returnText loading={loading} />
              )}
            </Flex>
          </Flex>

          <Flex style={{ minWidth: isTablet ? undefined : 100 }} vertical>
            <Text>
              ID: <Text copyable>{order.id}</Text>
            </Text>
            {order.outOfDraftDate && <Text type="secondary">{formateDate(order.outOfDraftDate)}</Text>}
          </Flex>
        </Flex>

        <Flex style={{ width: isTablet ? '100%' : '50%' }} justify="flex-start" align="flex-start">
          <Text copyable>{order.fullAddress}</Text>
        </Flex>
      </Flex>
    </ItemBox>
  )
})
